import React, { useEffect, useState } from 'react';
import {
	Box,
	Flex,
	Button,
	Text,
	Spinner,
	Grid,
	Image,
	useDisclosure,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { BonfireAPI, ApiType } from '../../../models/BonfireApi';
import config from '../../../utils/config';

interface Screenshot {
	name: string;
	url: string;
	lastModified: string;
	imageData?: string; // Blob URL
}

const IntegrationEventDetails: React.FC = () => {
	const { sessionId } = useParams<{ sessionId: string }>();
	const navigate = useNavigate();
	const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [selectedScreenshot, setSelectedScreenshot] = useState<Screenshot | null>(null);
	const { isOpen, onOpen, onClose } = useDisclosure();

	const fetchImageWithAuth = async (url: string, api: BonfireAPI): Promise<string> => {
		try {
			// Use the BonfireAPI's axios client which already has auth headers
			const response = await (api as any).client.get(url, {
				responseType: 'blob',
			});

			const blob = new Blob([response.data], {
				type: response.headers['content-type'] || 'image/png',
			});
			return URL.createObjectURL(blob);
		} catch (err) {
			console.error('Failed to fetch image:', err);
			return '';
		}
	};

	const fetchScreenshots = async () => {
		if (!sessionId) {
			return;
		}

		setIsLoading(true);
		const url = `${config.qa_api.url}/files/screenshots?sessionId=${sessionId}`;
		const api = new BonfireAPI(ApiType.QaApi);
		try {
			const response = await api.get(url);
			const screenshotsData = response || [];

			// Fetch all images with authentication
			const screenshotsWithImages = await Promise.all(
				screenshotsData.map(async (screenshot: Screenshot) => ({
					...screenshot,
					imageData: await fetchImageWithAuth(screenshot.url, api),
				})),
			);

			setScreenshots(screenshotsWithImages);
		} catch (err) {
			console.error('Failed to fetch screenshots', err);
			setScreenshots([]);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchScreenshots();

		// Cleanup blob URLs when component unmounts
		return () => {
			screenshots.forEach(screenshot => {
				if (screenshot.imageData && screenshot.imageData.startsWith('blob:')) {
					URL.revokeObjectURL(screenshot.imageData);
				}
			});
		};
	}, [sessionId]);

	const handleScreenshotClick = (screenshot: Screenshot) => {
		setSelectedScreenshot(screenshot);
		onOpen();
	};

	const formatDate = (dateString: string) => {
		try {
			return new Date(dateString).toLocaleString();
		} catch {
			return dateString;
		}
	};

	if (isLoading) {
		return (
			<Flex justify="center" align="center" h="50vh">
				<Spinner size="xl" />
			</Flex>
		);
	}

	return (
		<Box>
			<Breadcrumb mb="20px" fontSize="14px">
				<BreadcrumbItem>
					<BreadcrumbLink
						onClick={() => navigate('/integration-events')}
						cursor="pointer"
					>
						Integration Events
					</BreadcrumbLink>
				</BreadcrumbItem>
				<BreadcrumbItem isCurrentPage>
					<BreadcrumbLink>{sessionId}</BreadcrumbLink>
				</BreadcrumbItem>
			</Breadcrumb>

			<Flex alignItems="center" justifyContent="space-between" mb="23px">
				<Box>
					<Text fontSize="18px" fontWeight="700">
						Session Details
					</Text>
					<Text fontSize="14px" color="gray.600" mt="5px">
						Session ID: {sessionId}
					</Text>
				</Box>
				<Flex gap="10px">
					<Button
						variant="beSecondaryButton"
						onClick={() => navigate('/integration-events')}
					>
						Back to List
					</Button>
					<Button variant="beMainButton" onClick={fetchScreenshots}>
						Refresh
					</Button>
				</Flex>
			</Flex>

			<Box bg="white" p="20px" borderRadius="8px">
				<Text fontWeight="600" mb="20px">
					Screenshots ({screenshots.length})
				</Text>
				{screenshots.length === 0 ? (
					<Text color="gray.500">No screenshots found</Text>
				) : (
					<Grid templateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap="16px">
						{screenshots.map(screenshot => (
							<Box
								key={screenshot.name}
								borderRadius="8px"
								overflow="hidden"
								bg="gray.50"
								boxShadow="sm"
								cursor="pointer"
								_hover={{
									boxShadow: 'md',
									transform: 'scale(1.02)',
								}}
								transition="all 0.2s"
								onClick={() => handleScreenshotClick(screenshot)}
							>
								<Image
									src={screenshot.imageData || ''}
									alt={screenshot.name}
									w="100%"
									h="150px"
									objectFit="cover"
								/>
								<Box p="8px">
									<Text fontSize="12px" noOfLines={1}>
										{screenshot.name}
									</Text>
									<Text fontSize="10px" color="gray.500">
										{formatDate(screenshot.lastModified)}
									</Text>
								</Box>
							</Box>
						))}
					</Grid>
				)}
			</Box>

			{/* Screenshot Modal */}
			<Modal isOpen={isOpen} onClose={onClose} size="6xl">
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>{selectedScreenshot?.name}</ModalHeader>
					<ModalCloseButton />
					<ModalBody pb="20px">
						{selectedScreenshot && (
							<Box>
								<Text fontSize="sm" color="gray.500" mb="10px">
									Last Modified: {formatDate(selectedScreenshot.lastModified)}
								</Text>
								<Image
									src={selectedScreenshot.imageData || ''}
									alt={selectedScreenshot.name}
									w="100%"
									borderRadius="8px"
								/>
							</Box>
						)}
					</ModalBody>
				</ModalContent>
			</Modal>
		</Box>
	);
};

export default IntegrationEventDetails;
